import {useCallback} from 'react';
import {$createTextNode, $getSelection, $isRangeSelection, $setSelection} from 'lexical';
import {copyToClipboard} from '@lexical/clipboard';
import {useEventCallback} from 'usehooks-ts';
import {IPluginHooks} from '../ToolbarPlugin/types';
import {FilledFieldContextMenu} from '../Editor/utils';
import {useContextMenu} from '../../../../../../hooks/contextMenu/useContextMenu';
import {emailDialogID} from '../../../../testIDs';
import {INSERT_IMAGE_COMMAND} from '../../../../../Form/components/HTMLFormEditor/Editor/plugins/ImagesPlugin/commands';
import {readFileAsDataURL} from '../../../../utils/readerFileAsDataURL';
import {checkImageSize} from '../../../../utils/checkHtmlSize';

export const useCustomClipboardCommands = ({editor}: IPluginHooks) => {
    const handleCopyAndCut = useEventCallback((isDeleteWord?: boolean) => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                copyToClipboard(editor, null);
                isDeleteWord && selection.deleteWord(true);
            }
        });
    });

    const handlePaste = useEventCallback(async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.read) {
                const selection = editor.getEditorState().read(() => {
                    return $getSelection();
                });

                let textToPaste = '';

                const items = await navigator.clipboard.read();

                for (const item of items) {
                    for (const type of item.types) {
                        if (type.startsWith('image/')) {
                            const blob = await item.getType(type);
                            const src = await readFileAsDataURL(blob);
                            const isBiggerThanMaxSize = checkImageSize(src, '', blob.size);
                            if (!isBiggerThanMaxSize) {
                                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                                    src: src,
                                    altText: '',
                                    isDraggable: false
                                });
                            }
                            return;
                        } else if (type === 'text/plain') {
                            textToPaste = await item.getType(type).then(blob => blob.text());
                        }
                    }
                }

                editor.update(() => {
                    if (selection) {
                        $setSelection(selection.clone());
                        const textNode = $createTextNode(textToPaste);
                        selection.insertNodes([textNode]);
                    }
                });
            }
        } catch (error) {
            console.error('Error reading clipboard data:', error);
        }
    });

    function renderItem() {
        function createWrappedHandlers() {
            const wrappedHandlers = {
                handleCut: wrapperFunction(() => handleCopyAndCut(true)),
                handleCopy: wrapperFunction(handleCopyAndCut),
                handlePaste: wrapperFunction(handlePaste)
            };
            return wrappedHandlers;
        }

        function wrapperFunction(func: () => void) {
            return () => {
                func();
                closeContextMenu?.();
            };
        }

        return FilledFieldContextMenu(createWrappedHandlers());
    }

    const {openContextMenu, closeContextMenu, contextMenuComponent} = useContextMenu({
        menuData: renderItem(),
        portalSelector: emailDialogID
    });

    const handleContext = useCallback(
        async e => {
            e.stopPropagation();
            e.preventDefault();

            const target = e.target;
            openContextMenu(target, e.clientX, e.clientY);
        },
        [openContextMenu]
    );

    return {
        handlePaste,
        handleCopyAndCut,
        closeContextMenu,
        handleContext,
        contextMenuComponent
    } as const;
};
